import { Link } from 'react-router-dom';
import { ArrowRight, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WriterProjectOverview } from '@/hooks/useWriterProjectsOverview';
import { draftStatusChip, relativeTime } from './draftStatus';

interface ContinueWritingCardProps {
  project: WriterProjectOverview | null;
  loading?: boolean;
}

/**
 * The one hero-weight element on the page: resume the draft you touched last.
 * Review state only appears once the draft is linked to a published paper —
 * AI reviews attach to papers, never to Writer projects.
 */
const ContinueWritingCard = ({ project, loading }: ContinueWritingCardProps) => {
  if (loading) {
    return (
      <div className="rounded-2xl border border-border/60 bg-card p-8 mb-12 animate-pulse">
        <div className="h-3 w-28 rounded bg-muted mb-6" />
        <div className="h-7 w-2/3 rounded bg-muted mb-3" />
        <div className="h-4 w-1/3 rounded bg-muted" />
      </div>
    );
  }

  if (!project) return null;

  const chip = draftStatusChip(project.compileStatus);

  return (
    <section className="mb-12">
      <Link
        to={`/write/${project.id}`}
        className="group block rounded-2xl border border-border/60 bg-gradient-to-br from-primary/[0.06] via-card to-card p-8 transition-colors hover:border-primary/40"
      >
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-5">
          <FileText className="h-3.5 w-3.5" />
          <span>Continue writing</span>
        </div>

        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-foreground mb-3">
          {project.title || 'Untitled draft'}
        </h2>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs',
              chip.tone === 'attention'
                ? 'border-destructive/30 text-destructive'
                : 'border-border/60 text-muted-foreground',
            )}
          >
            {chip.live && (
              <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" aria-hidden />
            )}
            {chip.label}
          </span>
          <span aria-hidden>·</span>
          <span>Edited {relativeTime(project.updatedAt)}</span>
          {project.publishedPaperId && (
            <>
              <span aria-hidden>·</span>
              <span>Linked to a published paper</span>
            </>
          )}
        </div>

        <span className="mt-6 inline-flex items-center text-sm font-medium text-primary">
          Continue
          <ArrowRight className="h-4 w-4 ml-1.5 transition-transform group-hover:translate-x-0.5" />
        </span>
      </Link>
    </section>
  );
};

export default ContinueWritingCard;
